import React, { useState } from 'react';
import { useDashboard, usePositions, useRisk, useHealth, useWeek } from '@/hooks/useDashboardQueries';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { StatusBadge } from '@/components/common/StatusBadge';
import { PnlValue } from '@/components/common/PnlValue';
import { LiveEventItem } from '@/components/common/LiveEventItem';
import { ErrorState } from '@/components/common/ErrorState';
import { MetricCardSkeleton, TableSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { ResetCircuitBreakerModal } from '@/features/risk-health/ResetCircuitBreakerModal';
import { formatINR, formatTimestamp, clampPercentage } from '@/lib/utils';
import { NavigationTab, ROUTES } from '@/lib/constants';
import {
  LayoutDashboard,
  TrendingUp,
  Activity,
  ShieldCheck,
  ShieldAlert,
  Layers,
  ArrowRight,
  Database,
  RotateCcw,
} from 'lucide-react';

interface OverviewViewProps {
  onNavigateTab?: (tab: NavigationTab) => void;
}

export const OverviewView: React.FC<OverviewViewProps> = ({ onNavigateTab }) => {
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const {
    data: dashboard,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useDashboard();

  const {
    data: positionsData,
    isLoading: isPositionsLoading,
  } = usePositions();

  const { data: riskData } = useRisk();
  const { data: healthData } = useHealth();
  const { data: weekData } = useWeek();

  if (isDashboardLoading && !dashboard) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Overview & Telemetry"
          description="Real-time operations terminal monitoring paper trading executions, risk controls, and market sync."
          icon={LayoutDashboard}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <MetricCardSkeleton key={`sec-${i}`} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <TableSkeleton rows={4} cols={5} />
          </div>
          <div className="space-y-4">
            <TableSkeleton rows={3} cols={2} />
          </div>
        </div>
      </div>
    );
  }

  if (isDashboardError && !dashboard) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Terminal Overview"
          icon={LayoutDashboard}
        />
        <ErrorState
          title="Failed to load dashboard overview"
          message={dashboardError instanceof Error ? dashboardError.message : 'API connection error'}
          onRetry={() => refetchDashboard()}
        />
      </div>
    );
  }

  const pnl = dashboard?.pnl_summary;
  const botStatus = dashboard?.bot_status;
  const positionsSummary = dashboard?.positions_summary;
  const risk = riskData?.risk || dashboard?.risk_summary;
  const health = healthData?.health;
  const openPositions = positionsData?.positions || [];
  const recentTrades = dashboard?.recent_trades || [];
  const recentSignals = dashboard?.recent_signals || [];

  const isMarketOpen = botStatus?.market_status === 'OPEN';
  const isTradingAllowed = risk?.trading_allowed ?? true;
  const isKillSwitchActive = risk?.kill_switch_active ?? false;
  const isCircuitBreakerHalted = Boolean(pnl?.circuit_breaker_halted ?? risk?.circuit_breaker_halted ?? false);

  // Active Exposure calculations (honest summation across open positions)
  const openPositionsCount = positionsSummary?.open_positions_count ?? openPositions.length;
  const maxPositions = positionsSummary?.max_positions ?? 5;
  const availableCapacity = positionsSummary?.available_capacity ?? Math.max(0, maxPositions - openPositionsCount);
  const capacityUtilizationPct = maxPositions > 0 ? clampPercentage((openPositionsCount / maxPositions) * 100) : 0;
  const deployedCapital = openPositions.reduce(
    (sum, pos) => sum + ((pos.entry_price || 0) * (pos.quantity || 0)),
    0
  );

  // Daily Risk calculations
  const dailyLoss = risk?.daily_loss || 0;
  const dailyLossLimit = risk?.daily_loss_limit || 5000;
  const lossUtilizationPct = dailyLossLimit > 0 ? clampPercentage((Math.abs(dailyLoss) / dailyLossLimit) * 100) : 0;
  const tradesToday = risk?.trades_today ?? pnl?.total_trades ?? 0;
  const maxTradesPerDay = risk?.max_trades_per_day || 6;
  const consecutiveLosses = risk?.consecutive_losses || 0;
  const maxConsecutiveLosses = risk?.max_consecutive_losses || 3;

  // Win Rate calculation: guard against 0-trade distortion
  const winningTrades = pnl?.winning_trades || 0;
  const losingTrades = pnl?.losing_trades || 0;
  const totalCompletedTrades = winningTrades + losingTrades;
  const hasCompletedTrades = totalCompletedTrades > 0 || (pnl?.total_trades !== undefined && pnl.total_trades > 0);

  // Derived Operational Engine State
  let engineState: 'OPERATIONAL' | 'IDLE' | 'PAUSED' | 'HALTED' | 'DEGRADED' | 'ERROR' = 'OPERATIONAL';
  let engineStateVariant: 'emerald' | 'cyan' | 'amber' | 'crimson' | 'default' = 'emerald';
  let engineStateDot = 'bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.8)]';

  if (isCircuitBreakerHalted) {
    engineState = 'HALTED';
    engineStateVariant = 'crimson';
    engineStateDot = 'bg-rose-400 shadow-[0_0_8px_rgba(244,63,94,0.8)]';
  } else if (!isTradingAllowed || isKillSwitchActive) {
    engineState = 'PAUSED';
    engineStateVariant = 'amber';
    engineStateDot = 'bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.8)]';
  } else if (health?.status === 'ERROR') {
    engineState = 'ERROR';
    engineStateVariant = 'crimson';
    engineStateDot = 'bg-rose-400 shadow-[0_0_8px_rgba(244,63,94,0.8)]';
  } else if (health?.status === 'WARNING' || health?.status === 'STALE') {
    engineState = 'DEGRADED';
    engineStateVariant = 'amber';
    engineStateDot = 'bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.8)]';
  } else if (!isMarketOpen) {
    engineState = 'IDLE';
    engineStateVariant = 'default';
    engineStateDot = 'bg-slate-400';
  }

  const getLossBarColor = () => {
    if (lossUtilizationPct >= 90 || isCircuitBreakerHalted) return 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.6)]';
    if (lossUtilizationPct >= 70) return 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]';
    return 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]';
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Top Header */}
      <div className="animate-stagger-1">
        <PageHeader
          title="Terminal Overview"
          description="Autonomous paper-mode swing trading observability terminal. Live telemetry, P&L attribution, and risk boundaries."
          icon={LayoutDashboard}
          badge={
            <div className="flex items-center gap-2">
              <StatusBadge
                status={health?.status || 'HEALTHY'}
                label={`SYS: ${health?.status || 'HEALTHY'}`}
                size="sm"
              />
            </div>
          }
          actions={
            <div className="flex items-center gap-2 text-xs font-mono text-slate-400 bg-slate-900/90 border border-slate-800 px-3 py-1.5 rounded-lg shadow-sm">
              <Database className="w-3.5 h-3.5 text-emerald-400" />
              <span>DB: {health?.database_status || 'SQLite WAL'}</span>
            </div>
          }
        />
      </div>

      {/* Prominent Circuit Breaker Halt Notice and Action */}
      {isCircuitBreakerHalted && (
        <div className="p-3.5 rounded-lg border border-rose-800/80 bg-rose-950/40 text-xs font-mono animate-stagger-1">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <ShieldAlert className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                <span className="font-bold text-rose-300 uppercase tracking-wide">
                  CIRCUIT BREAKER: HALTED
                </span>
                <p className="text-[11px] text-rose-200/90 leading-relaxed">
                  New entries are blocked because today&apos;s circuit breaker is latched.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsResetModalOpen(true)}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white text-xs font-semibold shadow-md shadow-rose-950/50 transition-all active:scale-95 shrink-0"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reset Circuit Breaker</span>
            </button>
          </div>
        </div>
      )}

      {/* Row 1: Core Command Strip (4 Primary Operational Cards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-2">
        {/* Card 1: Today's Net P&L */}
        <MetricCard
          title="Today's Net P&L"
          value={
            <PnlValue
              value={pnl?.today_pnl}
              size="xl"
              showSign={true}
            />
          }
          subtitle={
            <div className="flex items-center justify-between w-full text-[11px] font-mono text-slate-400 pt-1 border-t border-slate-800/60">
              <span>
                Realized: <span className={pnl?.realized_pnl && pnl.realized_pnl > 0 ? 'text-emerald-400 font-semibold' : pnl?.realized_pnl && pnl.realized_pnl < 0 ? 'text-rose-400 font-semibold' : 'text-slate-300'}>{formatINR(pnl?.realized_pnl, { showSign: true })}</span>
              </span>
              <span>
                Unrealized: <span className={pnl?.unrealized_pnl && pnl.unrealized_pnl > 0 ? 'text-emerald-400 font-semibold' : pnl?.unrealized_pnl && pnl.unrealized_pnl < 0 ? 'text-rose-400 font-semibold' : 'text-slate-300'}>
                  {openPositionsCount === 0
                    ? '₹0.00'
                    : pnl?.unrealized_pnl === null || pnl?.unrealized_pnl === undefined
                    ? 'Pending'
                    : formatINR(pnl.unrealized_pnl, { showSign: true })}
                </span>
              </span>
            </div>
          }
          variant={pnl?.today_pnl && pnl.today_pnl > 0 ? 'emerald' : pnl?.today_pnl && pnl.today_pnl < 0 ? 'crimson' : 'default'}
          icon={TrendingUp}
        />

        {/* Card 2: Active Exposure */}
        <MetricCard
          title="Active Exposure"
          value={
            <span className="text-slate-100 font-mono">
              {formatINR(deployedCapital)}
            </span>
          }
          subtitle={
            <div className="space-y-1.5 w-full pt-1 border-t border-slate-800/60">
              <div className="flex items-center justify-between text-[11px] font-mono">
                <span className="text-slate-300 font-semibold tabular-nums">
                  {openPositionsCount} / {maxPositions} positions
                </span>
                <span className="text-cyan-400 font-bold tabular-nums">
                  {capacityUtilizationPct.toFixed(0)}% capacity
                </span>
              </div>
              <div
                className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={capacityUtilizationPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Position capacity utilization"
              >
                <div
                  className="h-full bg-cyan-500 rounded-full transition-all duration-500 shadow-[0_0_8px_rgba(6,182,212,0.6)]"
                  style={{ width: `${capacityUtilizationPct}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-[10px] font-mono text-slate-500">
                <span>{availableCapacity} slot{availableCapacity === 1 ? '' : 's'} available</span>
                <span>Max {maxPositions}</span>
              </div>
            </div>
          }
          variant={openPositionsCount > 0 ? 'cyan' : 'default'}
          icon={Layers}
        />

        {/* Card 3: Daily Risk */}
        <MetricCard
          title="Daily Risk"
          value={
            <div className="flex items-baseline justify-between gap-1 w-full">
              <span className="text-xl font-bold font-mono tracking-tight text-slate-100 tabular-nums">
                {formatINR(dailyLoss)}
                <span className="text-xs text-slate-500 font-normal ml-1">/ {formatINR(dailyLossLimit)}</span>
              </span>
              <span className={`text-xs font-mono font-bold tabular-nums ${lossUtilizationPct >= 90 ? 'text-rose-400' : lossUtilizationPct >= 70 ? 'text-amber-400' : 'text-emerald-400'}`}>
                {lossUtilizationPct.toFixed(1)}%
              </span>
            </div>
          }
          subtitle={
            <div className="space-y-1.5 w-full pt-1 border-t border-slate-800/60">
              <div
                className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={lossUtilizationPct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Daily risk limit utilization"
              >
                <div
                  className={`h-full rounded-full transition-all duration-500 ${getLossBarColor()}`}
                  style={{ width: `${lossUtilizationPct}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 tabular-nums">
                <span>Trades: <strong className="text-slate-200">{tradesToday}</strong>/{maxTradesPerDay}</span>
                <span>Consec Loss: <strong className="text-slate-200">{consecutiveLosses}</strong>/{maxConsecutiveLosses}</span>
              </div>
            </div>
          }
          variant={isCircuitBreakerHalted || lossUtilizationPct >= 90 ? 'crimson' : lossUtilizationPct >= 70 ? 'amber' : 'default'}
          icon={isCircuitBreakerHalted ? ShieldAlert : ShieldCheck}
        />

        {/* Card 4: Engine State */}
        <MetricCard
          title="Engine State"
          value={
            <div className="flex items-center gap-2 font-mono font-bold tracking-wide">
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${engineStateDot} ${engineState === 'OPERATIONAL' ? 'animate-pulse' : ''}`} />
              <span className={
                engineState === 'OPERATIONAL' ? 'text-emerald-400' :
                engineState === 'HALTED' || engineState === 'ERROR' ? 'text-rose-400' :
                engineState === 'PAUSED' || engineState === 'DEGRADED' ? 'text-amber-400' :
                'text-slate-300'
              }>
                {engineState}
              </span>
            </div>
          }
          subtitle={
            <div className="space-y-1 w-full pt-1 border-t border-slate-800/60 text-[11px] font-mono text-slate-400">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Mode • Market:</span>
                <span className="text-slate-200 font-semibold">{botStatus?.mode || 'PAPER'} • {botStatus?.market_status || 'CLOSED'}</span>
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-slate-500">Circuit Breaker:</span>
                <span className={isCircuitBreakerHalted ? 'text-rose-400 font-bold' : isKillSwitchActive ? 'text-rose-400 font-bold' : 'text-emerald-400 font-semibold'}>
                  {isCircuitBreakerHalted ? 'HALTED' : isKillSwitchActive ? 'TRIGGERED' : 'NORMAL'}
                </span>
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-slate-500">Last Cycle:</span>
                <span className="text-slate-300 tabular-nums">
                  {botStatus?.last_cycle ? formatTimestamp(botStatus.last_cycle, 'time') : 'Waiting Cycle'}
                </span>
              </div>
            </div>
          }
          variant={engineStateVariant}
          icon={Activity}
        />
      </div>

      {/* Row 2: Secondary Performance & Context Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3.5 rounded-lg border border-slate-800/80 bg-slate-950/40 text-xs font-mono animate-stagger-2">
        {/* Weekly P&L */}
        <div className="space-y-0.5 border-r border-slate-800/60 pr-2">
          <span className="text-[10px] uppercase text-slate-500 font-semibold block">
            WEEKLY P&L
          </span>
          <div className="text-sm font-bold">
            <PnlValue
              value={weekData?.weekly_pnl ?? pnl?.weekly_pnl}
              size="sm"
              showSign={true}
            />
          </div>
          <span className="text-[10px] text-slate-500 block truncate">
            Current calendar week
          </span>
        </div>

        {/* Win Rate */}
        <div className="space-y-0.5 sm:border-r border-slate-800/60 pr-2">
          <span className="text-[10px] uppercase text-slate-500 font-semibold block">
            WIN RATE
          </span>
          <div className="text-sm font-bold">
            {hasCompletedTrades ? (
              <span className="text-emerald-400">
                {pnl?.win_rate !== undefined && pnl.win_rate !== null ? `${pnl.win_rate.toFixed(1)}%` : '—'}
              </span>
            ) : (
              <span className="text-slate-400 font-normal">—</span>
            )}
          </div>
          <span className="text-[10px] text-slate-500 block truncate">
            {hasCompletedTrades
              ? `${winningTrades}W / ${losingTrades}L executed`
              : 'No completed trades'}
          </span>
        </div>

        {/* Trades Today */}
        <div className="space-y-0.5 border-r border-slate-800/60 pr-2">
          <span className="text-[10px] uppercase text-slate-500 font-semibold block">
            TRADES TODAY
          </span>
          <div className="text-sm font-bold text-slate-200 tabular-nums">
            {tradesToday}
            <span className="text-xs text-slate-500 font-normal ml-1">/ {maxTradesPerDay}</span>
          </div>
          <span className="text-[10px] text-slate-500 block truncate">
            Daily limit allocation
          </span>
        </div>

        {/* Last Signal */}
        <div className="space-y-0.5">
          <span className="text-[10px] uppercase text-slate-500 font-semibold block">
            LAST SIGNAL
          </span>
          <div className="text-sm font-bold text-slate-200 truncate">
            {botStatus?.last_signal ? formatTimestamp(botStatus.last_signal, 'time') : 'None'}
          </div>
          <span className="text-[10px] text-slate-500 block truncate">
            {botStatus?.last_signal ? 'Last signal received' : 'No signals today'}
          </span>
        </div>
      </div>

      {/* Row 3: Live Positions Snapshot & Real-Time Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-stagger-3">
        {/* Left 2 Cols: Open Positions Preview */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-slate-300 flex items-center gap-2">
              <Layers className="w-4 h-4 text-cyan-400" />
              Open Positions ({openPositions.length})
            </h3>
            {onNavigateTab && (
              <button
                onClick={() => onNavigateTab(ROUTES.POSITIONS)}
                className="text-xs font-mono text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors"
              >
                <span>Full Position Table</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {isPositionsLoading && openPositions.length === 0 ? (
            <TableSkeleton rows={3} cols={5} />
          ) : openPositions.length === 0 ? (
            <EmptyState
              title="No open positions"
              description="No open positions. The bot is waiting for the next eligible trading signal."
            />
          ) : (
            <div className="overflow-x-auto rounded-lg border border-slate-800/90 bg-slate-950/40">
              <table className="w-full text-left text-xs font-mono">
                <thead className="bg-slate-900/80 border-b border-slate-800 text-slate-400 uppercase text-[10px]">
                  <tr>
                    <th className="px-3 py-2.5">Symbol</th>
                    <th className="px-3 py-2.5 text-right">Qty</th>
                    <th className="px-3 py-2.5 text-right">Entry</th>
                    <th className="px-3 py-2.5 text-right">LTP</th>
                    <th className="px-3 py-2.5 text-right">Unrealized P&L</th>
                    <th className="px-3 py-2.5 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {openPositions.slice(0, 5).map((pos) => (
                    <tr key={pos.id} className="hover:bg-slate-900/50 transition-colors">
                      <td className="px-3 py-2.5 font-bold text-slate-200">
                        {pos.symbol}
                        <span className="text-[10px] text-slate-500 font-normal ml-1">
                          {pos.exchange}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-300 tabular-nums">
                        {pos.quantity}
                      </td>
                      <td className="px-3 py-2.5 text-right text-slate-300 tabular-nums">
                        {formatINR(pos.entry_price)}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {pos.ltp !== null && pos.ltp !== undefined ? (
                          <span className="text-slate-200">{formatINR(pos.ltp)}</span>
                        ) : (
                          <span className="text-[10px] text-amber-400/90 bg-amber-950/40 px-1.5 py-0.5 rounded border border-amber-900/50">
                            Pending Cycle
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        <PnlValue
                          value={pos.unrealized_pnl}
                          percent={pos.unrealized_pnl_percent}
                          showSign={true}
                          showPercent={true}
                          layout="stacked"
                          align="right"
                          nullPlaceholder="Pending Cycle"
                          size="sm"
                        />
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <StatusBadge status={pos.status} size="sm" showDot={false} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right 1 Col: Recent Executions & Signals Activity */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-mono font-bold uppercase tracking-wider text-slate-300 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-400" />
              Live Activity Stream
            </h3>
            {onNavigateTab && (
              <button
                onClick={() => onNavigateTab(ROUTES.SIGNALS)}
                className="text-xs font-mono text-emerald-400 hover:text-emerald-300 flex items-center gap-1 transition-colors"
              >
                <span>Signals & Events</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="space-y-2.5">
            {recentSignals.length === 0 && recentTrades.length === 0 ? (
              <EmptyState
                title="No recent events"
                description="Live executions and Telegram events will stream here automatically."
              />
            ) : (
              <>
                {recentTrades.slice(0, 2).map((trade) => (
                  <LiveEventItem
                    key={trade.id}
                    event={{
                      id: `trade-${trade.id}`,
                      type: 'trade_executed',
                      timestamp: trade.timestamp,
                      symbol: trade.symbol,
                      message: `${trade.side} ${trade.quantity} @ ${formatINR(trade.price)} (${trade.reason || trade.status})`,
                      severity: trade.pnl && trade.pnl < 0 ? 'warning' : 'success',
                    }}
                  />
                ))}
                {recentSignals.slice(0, 3).map((sig) => {
                  const status = String(sig.status || sig.validation_status || '');
                  const isOpened = status === 'POSITION_OPENED';
                  const isBlocked = status === 'BLOCKED_RISK';
                  const isOrderFail = ['ORDER_REJECTED', 'ORDER_FAILED'].includes(status);
                  const isExecUnknown = status === 'EXECUTION_UNKNOWN';
                  const isRejected = status === 'REJECTED';

                  let eventType = 'signal_received';
                  let severity: 'info' | 'success' | 'warning' | 'error' = 'info';

                  if (isOpened) {
                    eventType = 'position_opened';
                    severity = 'success';
                  } else if (isBlocked) {
                    eventType = 'signal_blocked';
                    severity = 'warning';
                  } else if (isOrderFail) {
                    eventType = status === 'ORDER_REJECTED' ? 'order_rejected' : 'order_failed';
                    severity = 'error';
                  } else if (isExecUnknown) {
                    eventType = 'execution_unknown';
                    severity = 'warning';
                  } else if (isRejected) {
                    eventType = 'signal_rejected';
                    severity = 'error';
                  }

                  const reason =
                    sig.outcome_reason ||
                    sig.rejection_reason ||
                    (sig.outcome_reason_code ? `Reason: ${sig.outcome_reason_code}` : null);
                  const message =
                    reason || (sig.signal_type ? `Signal ${sig.signal_type} (${status})` : `Status: ${status}`);

                  return (
                    <LiveEventItem
                      key={sig.id}
                      event={{
                        id: `sig-${sig.id}`,
                        type: eventType,
                        timestamp: sig.risk_decision_at || sig.timestamp || '',
                        symbol: sig.parsed_symbol,
                        message,
                        severity,
                      }}
                    />
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Circuit Breaker Reset Modal */}
      <ResetCircuitBreakerModal
        isOpen={isResetModalOpen}
        onClose={() => setIsResetModalOpen(false)}
        dailyStats={{
          trades_today: risk?.trades_today ?? 0,
          realized_pnl: pnl?.realized_pnl ?? 0,
          consecutive_losses: risk?.consecutive_losses ?? 0,
        }}
        limits={{
          max_trades_per_day: risk?.max_trades_per_day ?? 30,
          max_daily_loss: risk?.daily_loss_limit ?? 3000,
          max_consecutive_losses: risk?.max_consecutive_losses ?? 10,
        }}
      />
    </div>
  );
};
